// Mensajería interna. Centro Medico → Comunicación → Mensajes (y embebido en el portal).
// Hilos entre usuarios (médico–paciente–administrativo); polling para "tiempo real".
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, MessageSquarePlus, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { AutoTextarea, Campo, Modal, fieldCls, medRpc } from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface ConvRow { id: string; asunto: string | null; ultimo: string | null; ultima_fecha: string | null; no_leidas: number; participantes: string | null }
interface Msg { id: string; autor: string; mio: boolean; texto: string; created_at: string }
interface ConvDetalle { conversacion: { id: string; asunto: string | null }; participantes: string[]; mensajes: Msg[] }
interface UsuarioOpt { id: string; username: string; email: string | null; es_paciente: boolean }

export function Mensajes({ embedded = false }: { embedded?: boolean }) {
  const qc = useQueryClient();
  const [sel, setSel] = useState<string | null>(null);
  const [nueva, setNueva] = useState(false);

  const { data: convs = [], isFetching } = useQuery<ConvRow[]>({
    queryKey: ['med-convs'], queryFn: () => medRpc<ConvRow[]>('med_conversaciones', {}), refetchInterval: 8000,
  });

  return (
    <div className={embedded ? '' : 'p-6'}>
      <div className="flex items-center justify-between mb-3">
        <h1 className={embedded ? 'text-base font-semibold' : 'text-xl font-semibold'}>Mensajes</h1>
        <Button size="sm" onClick={() => setNueva(true)}><MessageSquarePlus className="w-4 h-4 mr-1" /> Nueva</Button>
      </div>
      <Card className="overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-3" style={{ height: embedded ? '24rem' : '32rem' }}>
          {/* Lista */}
          <div className="border-r overflow-y-auto">
            {isFetching && convs.length === 0 && <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" /></div>}
            {convs.length === 0 && !isFetching && <p className="p-4 text-sm text-muted-foreground">Sin conversaciones</p>}
            {convs.map((c) => (
              <button key={c.id} onClick={() => setSel(c.id)} className={`w-full border-b px-3 py-2 text-left text-sm hover:bg-muted/50 ${sel === c.id ? 'bg-muted' : ''}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium truncate">{c.asunto || c.participantes || 'Conversación'}</span>
                  {Number(c.no_leidas) > 0 && <span className="ml-2 rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">{c.no_leidas}</span>}
                </div>
                <div className="text-xs text-muted-foreground truncate">{c.participantes}</div>
                {c.ultimo && <div className="text-xs text-muted-foreground truncate">{c.ultimo}</div>}
              </button>
            ))}
          </div>
          {/* Hilo */}
          <div className="md:col-span-2 flex flex-col">
            {sel ? <Hilo id={sel} onSent={() => qc.invalidateQueries({ queryKey: ['med-convs'] })} />
              : <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">Selecciona una conversación</div>}
          </div>
        </div>
      </Card>

      {nueva && <NuevaConversacion onClose={() => setNueva(false)} onCreated={(id) => { setNueva(false); setSel(id); void qc.invalidateQueries({ queryKey: ['med-convs'] }); }} />}
    </div>
  );
}

function Hilo({ id, onSent }: { id: string; onSent: () => void }) {
  const qc = useQueryClient();
  const { data, isFetching } = useQuery<ConvDetalle>({
    queryKey: ['med-conv', id], queryFn: () => medRpc<ConvDetalle>('med_conversacion', { _id: id }), refetchInterval: 5000,
  });
  const [texto, setTexto] = useState('');
  const finRef = useRef<HTMLDivElement>(null);
  const nMsg = data?.mensajes.length ?? 0;
  useEffect(() => { finRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [nMsg]);
  const enviar = useMutation({
    mutationFn: () => medRpc('med_msg_enviar', { _conversacion_id: id, _texto: texto }),
    onSuccess: () => { setTexto(''); void qc.invalidateQueries({ queryKey: ['med-conv', id] }); onSent(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <>
      <div className="border-b px-3 py-2 text-sm font-medium">{data?.conversacion.asunto || data?.participantes.join(', ') || 'Conversación'}</div>
      <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
        {isFetching && !data && <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" /></div>}
        {data?.mensajes.map((m) => (
          <div key={m.id} className={`max-w-[80%] rounded-lg px-2.5 py-1.5 text-sm ${m.mio ? 'bg-primary/10 ml-auto' : 'bg-muted mr-auto'}`}>
            {!m.mio && <div className="text-[11px] font-medium text-muted-foreground">{m.autor}</div>}
            {m.texto}
            <div className="text-[10px] text-muted-foreground text-right">{new Date(m.created_at).toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' })}</div>
          </div>
        ))}
        <div ref={finRef} />
      </div>
      <form className="flex gap-1 border-t p-2" onSubmit={(e) => { e.preventDefault(); if (texto.trim()) enviar.mutate(); }}>
        <input className={`${fieldCls} h-9`} placeholder="Escribe un mensaje…" value={texto} onChange={(e) => setTexto(e.target.value)} />
        <Button type="submit" size="sm" isLoading={enviar.isPending} disabled={!texto.trim()}><Send className="w-4 h-4" /></Button>
      </form>
    </>
  );
}

function NuevaConversacion({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [busqueda, setBusqueda] = useState('');
  const { data: usuarios = [] } = useQuery<UsuarioOpt[]>({
    queryKey: ['med-usuarios', busqueda], queryFn: () => medRpc<UsuarioOpt[]>('med_usuarios', { _busqueda: busqueda }),
  });
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [asunto, setAsunto] = useState('');
  const [texto, setTexto] = useState('');
  const toggle = (id: string) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const crear = useMutation({
    mutationFn: () => medRpc<{ id: string }>('med_crear_conversacion', { _participantes: [...sel], _asunto: asunto || null, _texto: texto }),
    onSuccess: (r) => { toast.success('Conversación creada'); onCreated(r.id); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal title="Nueva conversación" onClose={onClose}>
      <div className="space-y-3">
        <Campo label="Buscar destinatarios"><input className={fieldCls} placeholder="Nombre o email…" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} /></Campo>
        <div className="max-h-40 overflow-y-auto border rounded-md divide-y">
          {usuarios.length === 0 && <p className="p-3 text-sm text-muted-foreground">Sin usuarios</p>}
          {usuarios.map((u) => (
            <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-muted/50">
              <input type="checkbox" checked={sel.has(u.id)} onChange={() => toggle(u.id)} className="h-4 w-4" />
              <span className="flex-1">{u.username} {u.es_paciente && <span className="text-xs text-muted-foreground">· paciente</span>}</span>
            </label>
          ))}
        </div>
        <Campo label="Asunto"><input className={fieldCls} value={asunto} onChange={(e) => setAsunto(e.target.value)} /></Campo>
        <Campo label="Mensaje"><AutoTextarea value={texto} onChange={(e) => setTexto(e.target.value)} /></Campo>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={crear.isPending} disabled={sel.size === 0 || !texto.trim()} onClick={() => crear.mutate()}>Crear</Button>
        </div>
      </div>
    </Modal>
  );
}
